<template>
  <transition-group name="fade">
    <div
      v-if="show"
      key="tag-dropdown"
      class="tag-dropdown flex flex-col"
    >
      <div
        class="border-b border-border-normal p-1 overflow-y-auto scroll-container"
        style="max-height: 200px;"
      >
        <div v-if="filteredTags?.length > 0">
          <button
            v-for="(option, index) in filteredTags"
            :key="`dropdown-option-${index}`"
            class="p-1.5 rounded-md hover:bg-background-hover transition-colors flex items-center gap-2 w-full"
            @click="$emit('selectTag', option)"
          >
            <TagIcon
              :stroke="tagColors[option?.color].stroke"
              :fill="tagColors[option?.color].fill"
            />
            <BaseText
              size="sm"
              class="text-text-muted"
            >
              {{ option.value }}
            </BaseText>
          </button>
        </div>
        <button
          class="p-1.5 rounded-md hover:bg-background-hover transition-colors flex items-center gap-1 w-full flex-nowrap"
          :class="{ 'opacity-75 cursor-not-allowed': isCreatingTag }"
          :disabled="isCreatingTag"
          @click="createNewTag"
        >
          <PlusIcon class="w-4 h-4 text-text-muted" />
          <BaseText
            size="sm"
            class="text-text-muted text-center"
          >
            {{ isCreatingTag ? 'Creating tag' : 'Create tag' }}
          </BaseText>
          <BaseText
            size="sm"
            class="text-text-subdued truncate whitespace-nowrap"
            style="max-width:80px"
          >
            {{ search }}
          </BaseText>
          <BaseLoadingSpinnerCircle
            v-if="isCreatingTag"
            class="w-3.5 h-3.5 text-icon-subdued ml-auto flex-shrink-0"
          />
        </button>
      </div>
      <!-- Edit tag button -->
      <div class="p-1">
        <button
          class="rounded-md hover:bg-background-hover transition-colors text-center w-full px-2 py-1.5"
          @click="enableEditTagModal"
        >
          <BaseText
            type="label"
            size="sm"
            class="text-text-muted leading-none"
          >
            Edit tags
          </BaseText>
        </button>
      </div>
    </div>
  </transition-group>
</template>

<script>
import LensAPI from '@/api/lens'
import { mapGetters, mapMutations } from 'vuex'
import LensTagModal from './LensTagModal.vue'
import TAG_COLORS from '../../utils/TagColors'
// Icons
import TagIcon from '../globals/Icons/TagIcon.vue'
import PlusIcon from '../globals/Icons/PlusIcon.vue'

export default {
  name: 'LensTagPopover',
  components: {
    LensTagModal,
    TagIcon,
    PlusIcon
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    search: {
      type: String,
      default: ''
    },
    selectedTags: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      tagColors: TAG_COLORS,
      isCreatingTag: false
    }
  },
  computed: {
    ...mapGetters('LensModule', ['getLensTags', 'getShowEditTagModal']),
    filteredTags () {
      const lensTags = this.selectedTags.length > 0 ? this.getLensTags.filter(tag => !this.selectedTags.some(selectedTag => selectedTag.tag_id === tag.tag_id)) : this.getLensTags
      return this.search.length > 0 ? lensTags.filter(tag => tag?.value.toLowerCase().includes(this.search.toLowerCase())) : lensTags
    }
  },
  methods: {
    ...mapMutations('LensModule', ['SET_TAGS', 'SET_SHOW_EDIT_TAG_MODAL']),
    async createNewTag () {
      if (this.isCreatingTag) return
      this.isCreatingTag = true
      this.$emit('create-tag-start')
      try {
        const tagToAdd = {
          lensId: this.$route.params?.lensId,
          value: this.search,
          color: Object.keys(this.tagColors)[Math.floor(Math.random() * Object.keys(this.tagColors).length)]
        }
        const { data } = await LensAPI.Tags.create(tagToAdd)
        this.SET_TAGS([...this.getLensTags, tagToAdd])
        this.$emit('selectTag', data)
        this.$showAlert({ type: 'success', message: 'Tag created successfully' })
      } catch (error) {
        console.error('Error creating tag', error)
        this.$showAlert({ type: 'error', message: 'Failed to create tag' })
      } finally {
        this.isCreatingTag = false
        this.$emit('create-tag-end')
      }
    },
    enableEditTagModal () {
      this.SET_SHOW_EDIT_TAG_MODAL(true)
    }
  }

}
</script>

<style scoped>
.tag-dropdown {
  position: absolute;
  top: calc(100%);
  left: 0;
  width: 200px;
  border-radius: 10px;
  background: #FFF;
  box-shadow: 0px 24px 32px -12px rgba(53, 57, 75, 0.12), 0px 1px 2px 0px rgba(162, 172, 186, 0.08), 0px 0px 0px 1px rgba(0, 56, 108, 0.04), 0px 1px 2px 0px rgba(162, 172, 186, 0.16), 0px 0px 0px 1px #EBEFF3;
  backdrop-filter: blur(5px);
  z-index: 9999;

}
.fade-enter-active,
.fade-leave-active {
  transition: all 150ms;
}

.fade-enter,
.fade-leave-to .fade-leave-active {
  opacity: 0;
  transform: translateY(-1rem);
}

/* Scrollbar styles */
.scroll-container::-webkit-scrollbar {
  height: 16px;
}

.scroll-container::-webkit-scrollbar-track {
  background: transparent;
}

.scroll-container::-webkit-scrollbar-thumb {
  background-color: rgba(236, 239, 243, 1);
  border-radius: 10px;
  border: 5px solid #ffffff;
  cursor: pointer;
  transition: border-color 150ms ease-in-out, border-width 150ms ease-in-out;
}

.scroll-container::-webkit-scrollbar-thumb:hover {
  background-color: rgba(223, 225, 231, 1);
  border-width: 4px;
}

.scroll-container {
  transition: all 150ms;
  scroll-behavior: smooth;
}
</style>
